/**
 * A user's groups: tick the groups they belong to, save. The container turns
 * the difference into membership adds and removes.
 */
import type { GroupWire } from '@acp/api-contract/tenancy.js';
import { useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { GroupChecklist } from './GroupChecklist.js';

export type UserGroupsDialogProps = {
  readonly open: boolean;
  readonly email: string;
  readonly groups: readonly GroupWire[];
  readonly current: readonly string[];
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSave: (groupIds: readonly string[]) => void;
  readonly onOpenChange: (open: boolean) => void;
};

function Body({ email, groups, current, pending, error, onSave, onOpenChange }: Omit<UserGroupsDialogProps, 'open'>) {
  const [selected, setSelected] = useState<string[]>([...current]);
  const changed = selected.length !== current.length || selected.some((id) => !current.includes(id));
  return (
    <div className="grid gap-4">
      <DialogHeader>
        <DialogTitle>Groups of {email}</DialogTitle>
        <DialogDescription>A user holds every permission of every group they are in, each within its group’s scope.</DialogDescription>
      </DialogHeader>
      <GroupChecklist groups={groups} selected={selected} onChange={setSelected} />
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={() => onSave(selected)} disabled={!changed || pending} data-testid="save-user-groups">
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </div>
  );
}

export function UserGroupsDialog(props: UserGroupsDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>{props.open ? <Body {...props} /> : null}</DialogContent>
    </Dialog>
  );
}
